import { mediaQueryLarge, requestIdleCallback, startViewTransition } from '@theme/utilities';
import PaginatedList from '@theme/paginated-list';

/**
 * A custom element that renders a pagniated results list
 */
export default class ResultsList extends PaginatedList {
  connectedCallback() {
    super.connectedCallback();

    mediaQueryLarge.addEventListener('change', this.#handleMediaQueryChange);
    this.#syncLayoutForViewport(mediaQueryLarge.matches ? 'desktop' : 'mobile');
    this.setAttribute('initialized', '');
  }

  disconnectedCallback() {
    mediaQueryLarge.removeEventListener('change', this.#handleMediaQueryChange);
  }

  /**
   * Updates the layout.
   *
   * @param {Event} event
   */
  updateLayout({ target }) {
    if (!(target instanceof HTMLInputElement)) return;

    this.#animateLayoutChange(target.value);
  }

  /**
   * Sets the layout.
   *
   * @param {string} value
   */
  #animateLayoutChange = async (value) => {
    const { grid } = this.refs;

    if (!grid) return;

    await startViewTransition(() => this.#setLayout(value), ['product-grid']);

    requestIdleCallback(() => {
      const viewport = mediaQueryLarge.matches ? 'desktop' : 'mobile';
      sessionStorage.setItem(`product-grid-view-${viewport}`, value);
    });
  };

  /**
   * Animates the layout change.
   *
   * @param {string} value
   */
  #setLayout(value) {
    const { grid } = this.refs;
    if (!grid) return;
    grid.setAttribute('product-grid-view', value);
  }

  /**
   * Syncs the grid and matching controls for the active viewport.
   * In the theme editor on desktop, always default to the large-card view.
   *
   * @param {'desktop' | 'mobile'} viewport
   */
  #syncLayoutForViewport(viewport) {
    const { grid } = this.refs;
    if (!grid) return;

    const shouldRestoreStoredLayout = !(window.Shopify?.designMode && viewport === 'desktop');
    const storedLayout = shouldRestoreStoredLayout
      ? sessionStorage.getItem(`product-grid-view-${viewport}`)
      : null;

    let layoutToUse;
    if (storedLayout) {
      layoutToUse = storedLayout;
    } else if (viewport === 'mobile' && grid?.dataset?.mobileDefault) {
      layoutToUse = grid.dataset.mobileDefault;
    } else {
      layoutToUse = 'default';
    }

    let targetElement;
    if (viewport === 'desktop') {
      targetElement =
        this.querySelector(`input[name="grid"][value="${layoutToUse}"]`) ||
        this.querySelector('[data-grid-layout="desktop-default-option"]');
    } else {
      targetElement =
        this.querySelector(`input[name="grid-mobile"][value="${layoutToUse}"]`) ||
        this.querySelector('[data-grid-layout="mobile-option"]');
    }

    if (targetElement instanceof HTMLInputElement) {
      targetElement.checked = true;
    }

    this.#setLayout(layoutToUse);
  }

  /**
   * Handles the media query change event.
   *
   * @param {MediaQueryListEvent} event
   */
  #handleMediaQueryChange = (event) => {
    this.#syncLayoutForViewport(event.matches ? 'desktop' : 'mobile');
  };
}

if (!customElements.get('results-list')) {
  customElements.define('results-list', ResultsList);
}
